import { Check, Minus } from "lucide-react";
import type { Actions, AppAbility } from "@adva/auth";
import { subject as caslSubject } from "@casl/ability";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// ADV-896: shared permissions grid for the settings "My permissions" card and
// the /dev/abilities diagnostic page. Renders a subject × action matrix where
// each cell shows ✓/— for whether the supplied ability grants the action.
//
// The cells call `ability.can(action, subject({...}))` rather than
// `ability.can(action, "Subject")` so the CASL rule conditions (account_id /
// business_id) actually evaluate. With a bare subject *type* string CASL
// matches if ANY rule grants the action — that's the same defence-in-depth
// concern that prompted the requireAbility fix in PR #633.

interface AbilityScope {
  accountId: string;
  businessId: string | null;
  userId: string | null;
}

interface SubjectRow {
  type: SubjectType;
  label: string;
  /** Verbs other than the four primary action columns. Rendered comma-separated. */
  extras?: Actions[];
  /**
   * Some action+subject combos are not meaningful in the matrix (e.g. no
   * "delete Account" exists in any rule). Marking those here renders a "—" in
   * the cell instead of ✗ — keeps the grid readable.
   */
  notApplicable?: Actions[];
}

type SubjectType =
  | "Account"
  | "Business"
  | "User"
  | "Job"
  | "Customer"
  | "Invoice"
  | "ApiKey"
  | "Opportunity"
  | "Proposal"
  | "TeamMember"
  | "Brand"
  | "Crew";

const ROWS: SubjectRow[] = [
  {
    type: "Account",
    label: "Account",
    notApplicable: ["create", "delete"],
  },
  {
    type: "Business",
    label: "Business",
    extras: ["reset", "backup", "restore"],
  },
  { type: "User", label: "User" },
  { type: "Job", label: "Job" },
  { type: "Customer", label: "Customer" },
  { type: "Opportunity", label: "Opportunity" },
  { type: "Proposal", label: "Proposal" },
  { type: "TeamMember", label: "Team Member" },
  { type: "Brand", label: "Brand" },
  { type: "Crew", label: "Crew" },
  {
    type: "Invoice",
    label: "Invoice",
    notApplicable: ["create", "update", "delete"],
  },
  {
    type: "ApiKey",
    label: "API Key",
    extras: ["revoke"],
    notApplicable: ["update", "delete"],
  },
];

const PRIMARY_ACTIONS: Actions[] = ["read", "create", "update", "delete"];

function buildSubject(type: SubjectType, scope: AbilityScope) {
  // The `id` condition key means different things per subject:
  //   - Account → rule conditions match `{ id: accountId }`
  //   - User    → rule conditions on User are keyed off `account_id`, but a
  //               consumer doing "can I read myself?" would key off `id: userId`
  //   - Others  → rules don't condition on `id` directly (they use account_id
  //               or business_id), so the value here is incidental
  // Account checks fail if we send `id: userId`, so map explicitly per type.
  let id: string | undefined;
  if (type === "Account") id = scope.accountId;
  else if (type === "User") id = scope.userId ?? undefined;

  return caslSubject(type, {
    id,
    account_id: scope.accountId,
    business_id: scope.businessId ?? undefined,
  });
}

interface PermissionsGridProps {
  ability: AppAbility;
  scope: AbilityScope;
}

export function PermissionsGrid({ ability, scope }: PermissionsGridProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Subject</TableHead>
          {PRIMARY_ACTIONS.map((action) => (
            <TableHead key={action} className="capitalize text-center">
              {action}
            </TableHead>
          ))}
          <TableHead>Other</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {ROWS.map((row) => {
          const subject = buildSubject(row.type, scope);
          const extrasAllowed = (row.extras ?? []).filter((a) =>
            ability.can(a, subject),
          );
          return (
            <TableRow key={row.type}>
              <TableCell className="font-medium">{row.label}</TableCell>
              {PRIMARY_ACTIONS.map((action) => {
                if (row.notApplicable?.includes(action)) {
                  return (
                    <TableCell
                      key={action}
                      className="text-center text-[color:var(--color-text-lo)]"
                    >
                      <Minus className="inline h-4 w-4" aria-label="n/a" />
                    </TableCell>
                  );
                }
                const allowed = ability.can(action, subject);
                return (
                  <TableCell key={action} className="text-center">
                    {allowed ? (
                      <Check
                        className="inline h-4 w-4 text-[color:var(--color-success,#15803d)]"
                        aria-label={`can ${action}`}
                      />
                    ) : (
                      <Minus
                        className="inline h-4 w-4 text-[color:var(--color-text-lo)]"
                        aria-label={`cannot ${action}`}
                      />
                    )}
                  </TableCell>
                );
              })}
              <TableCell className="typo-mono text-xs text-[color:var(--color-text-md)]">
                {extrasAllowed.length > 0 ? extrasAllowed.join(", ") : "—"}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
